import { useCallback, useMemo, useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need convex api
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need convex dataModel types
import { toast } from 'sonner';
import type {
  IKBDocumentRow,
  IKBTextEntryInput,
  IKBWebsiteCrawlInput,
  IKnowledgeBaseDocument,
} from '@/@types/knowledgeBase';

// ─── SHA-256 client-side helper ──────────────────────────────────────────────

async function computeSha256(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useKnowledgeBase(orgId: Id<'organisations'> | undefined) {
  const [selectedIds, setSelectedIds] = useState<Set<Id<'knowledgeBaseDocuments'>>>(new Set());

  // ─── Queries ──────────────────────────────────────────────────────────────

  const rawDocuments = useQuery(api.knowledgeBase.queries.list, orgId ? { orgId } : 'skip');

  // ─── Mutations ────────────────────────────────────────────────────────────

  const batchRemoveDocuments = useMutation(api.knowledgeBase.mutations.batchRemoveDocuments);
  const updateSharedStatus = useMutation(api.knowledgeBase.mutations.updateSharedStatus);
  const createTextEntry = useMutation(api.knowledgeBase.mutations.createTextEntry);
  const createWebsiteEntry = useMutation(api.knowledgeBase.mutations.createWebsiteEntry);
  const processTextEntry = useAction(api.knowledgeBase.processing.processTextEntry);
  const crawlWebsite = useAction(api.knowledgeBase.crawl.crawlWebsite);

  // ─── Computed / Mapped Rows ───────────────────────────────────────────────

  const documents: IKBDocumentRow[] = useMemo(() => {
    if (!rawDocuments) return [];
    return rawDocuments.map((doc: IKnowledgeBaseDocument) => ({
      _id: doc._id,
      title: doc.title,
      sourceType: doc.sourceType,
      fileName: doc.fileName,
      fileType: doc.fileType,
      websiteUrl: doc.websiteUrl,
      isSharedWithCompanies: doc.isSharedWithCompanies,
      processingStatus: doc.processingStatus,
      createdAt: doc.createdAt,
    }));
  }, [rawDocuments]);

  const fileDocuments = useMemo(() => documents.filter(d => d.sourceType === 'file'), [documents]);
  const textDocuments = useMemo(() => documents.filter(d => d.sourceType === 'text'), [documents]);
  const websiteDocuments = useMemo(
    () => documents.filter(d => d.sourceType === 'website'),
    [documents]
  );

  // ─── Selection ────────────────────────────────────────────────────────────

  const handleSelectRow = useCallback(
    (documentId: Id<'knowledgeBaseDocuments'>, selected: boolean) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (selected) {
          next.add(documentId);
        } else {
          next.delete(documentId);
        }
        return next;
      });
    },
    []
  );

  const handleSelectAll = useCallback((rows: IKBDocumentRow[], selected: boolean) => {
    if (selected) {
      setSelectedIds(new Set(rows.map(d => d._id)));
    } else {
      setSelectedIds(new Set());
    }
  }, []);

  const isAllSelected = useCallback(
    (rows: IKBDocumentRow[]) => rows.length > 0 && selectedIds.size === rows.length,
    [selectedIds]
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // ─── CRUD Handlers ────────────────────────────────────────────────────────

  const handleDeleteSelected = useCallback(
    async (ids: Id<'knowledgeBaseDocuments'>[]) => {
      if (ids.length === 0) return;
      try {
        await batchRemoveDocuments({ documentIds: ids });
        setSelectedIds(new Set());
        toast.success(`Deleted ${ids.length} document${ids.length > 1 ? 's' : ''}`);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete documents';
        toast.error(message);
      }
    },
    [batchRemoveDocuments]
  );

  const handleToggleShared = useCallback(
    async (documentId: Id<'knowledgeBaseDocuments'>, shared: boolean) => {
      try {
        await updateSharedStatus({ documentId, isSharedWithCompanies: shared });
        toast.success(shared ? 'Document shared with companies' : 'Document unshared');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to update sharing';
        toast.error(message);
      }
    },
    [updateSharedStatus]
  );

  /**
   * Signal reprocess intent. Actual action dispatch (processFile / processTextEntry /
   * crawlWebsite) must be done by the caller via useAction — actions cannot be invoked
   * through useMutation. Returns the document ID so the caller can trigger the action.
   */
  const handleReprocess = useCallback(
    (documentId: Id<'knowledgeBaseDocuments'>): Id<'knowledgeBaseDocuments'> | undefined => {
      if (!orgId) return undefined;
      const doc = rawDocuments?.find((d: IKnowledgeBaseDocument) => d._id === documentId);
      if (!doc) return undefined;
      toast.info('Reprocessing queued — refresh in a moment');
      return documentId;
    },
    [orgId, rawDocuments]
  );

  const handleSaveText = useCallback(
    async (input: IKBTextEntryInput): Promise<Id<'knowledgeBaseDocuments'> | undefined> => {
      if (!orgId) return undefined;
      try {
        const contentHash = await computeSha256(input.content);
        const documentId = await createTextEntry({
          orgId,
          title: input.title,
          originalContent: input.content,
          contentHash,
          isSharedWithCompanies: input.isSharedWithCompanies,
        });

        // Fire-and-forget: trigger RAG ingestion
        void processTextEntry({
          documentId,
          orgId,
          title: input.title,
          content: input.content,
          contentHash,
          isSharedWithCompanies: input.isSharedWithCompanies,
        });

        toast.success('Text entry saved — processing will begin shortly');
        return documentId;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save text entry';
        toast.error(message);
        throw error;
      }
    },
    [orgId, createTextEntry, processTextEntry]
  );

  const handleCrawlWebsite = useCallback(
    async (input: IKBWebsiteCrawlInput): Promise<Id<'knowledgeBaseDocuments'> | undefined> => {
      if (!orgId) return undefined;
      try {
        const documentId = await createWebsiteEntry({
          orgId,
          title: input.url,
          websiteUrl: input.url,
          websiteCrawlDepth: input.depth,
          isSharedWithCompanies: input.isSharedWithCompanies,
        });

        // Fire-and-forget: trigger Firecrawl crawling + RAG ingestion
        void crawlWebsite({
          documentId,
          orgId,
          url: input.url,
          maxDepth: input.depth,
          title: input.url,
          isSharedWithCompanies: input.isSharedWithCompanies,
        });

        toast.success('Website crawl started — this may take a few minutes');
        return documentId;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to start website crawl';
        toast.error(message);
        throw error;
      }
    },
    [orgId, createWebsiteEntry, crawlWebsite]
  );

  const handleRefreshWebsite = useCallback(
    async (documentId: Id<'knowledgeBaseDocuments'>): Promise<void> => {
      if (!orgId) return;
      const doc = rawDocuments?.find((d: IKnowledgeBaseDocument) => d._id === documentId);
      if (!doc?.websiteUrl) return;

      void crawlWebsite({
        documentId,
        orgId,
        url: doc.websiteUrl,
        maxDepth: doc.websiteCrawlDepth ?? 3,
        title: doc.title,
        isSharedWithCompanies: doc.isSharedWithCompanies,
      });

      toast.info('Website re-crawl started — this may take a few minutes');
    },
    [orgId, rawDocuments, crawlWebsite]
  );

  // ─── Return ───────────────────────────────────────────────────────────────

  return {
    documents,
    fileDocuments,
    textDocuments,
    websiteDocuments,
    isLoading: rawDocuments === undefined,
    selectedIds,
    handleSelectRow,
    handleSelectAll,
    isAllSelected,
    clearSelection,
    handleDeleteSelected,
    handleToggleShared,
    handleReprocess,
    handleSaveText,
    handleCrawlWebsite,
    handleRefreshWebsite,
  };
}
